/**
 * Vérification en deux étapes à la connexion.
 *
 * Les deux méthodes de la V1 sont proposées, avec l'étiquette attendue par le
 * produit : « Application d'authentification — Recommandé » et « Code par
 * e-mail — Standard ». Les codes de récupération servent de secours.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { authApi } from "../api/endpoints";
import { AuthLayout } from "../components/Layout";
import { Alert, Button, TextField } from "../components/ui";
import { useSession } from "../hooks/SessionContext";
import { useDocumentTitle, useMutation } from "../hooks/useApi";

type Method = "totp" | "email" | "recovery";

const METHOD_LABELS: Record<Method, { title: string; tag: string; hint: string }> = {
  totp: {
    title: "Application d'authentification",
    tag: "Recommandé",
    hint: "Saisissez le code à 6 chiffres affiché par votre application.",
  },
  email: {
    title: "Code par e-mail",
    tag: "Standard",
    hint: "Saisissez le code à 6 chiffres reçu par e-mail.",
  },
  recovery: {
    title: "Code de récupération",
    tag: "Secours",
    hint: "Saisissez l'un de vos codes de récupération (format XXXX-XXXX-XXXX).",
  },
};

export function TwoFactorChallengePage() {
  useDocumentTitle("Vérification en deux étapes");
  const navigate = useNavigate();
  const { state, applyState } = useSession();
  const challenge = state?.twofa_challenge;
  const available = (challenge?.available_methods ?? []) as Method[];

  const [method, setMethod] = useState<Method>(
    (challenge?.preferred_method as Method | undefined) ?? available[0] ?? "totp",
  );
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);

  useEffect(() => {
    setCode("");
  }, [method]);

  const verify = useMutation(
    () => authApi.verifyTwoFactor({ method, code: code.trim() }),
    {
      onSuccess: (next) => {
        applyState(next);
        navigate(next.businesses.length === 0 ? "/bienvenue" : "/", {
          replace: true,
        });
      },
    },
  );

  const sendCode = useMutation(() => authApi.sendTwoFactorCode(), {
    onSuccess: () => setCodeSent(true),
  });

  const info = METHOD_LABELS[method];
  const choices: Method[] = [...available];
  if (!choices.includes("recovery")) choices.push("recovery");

  return (
    <AuthLayout
      title="Vérification en deux étapes"
      intro="Pour protéger votre compte, une seconde vérification est demandée."
      footer={
        <Button
          variant="quiet"
          size="sm"
          onClick={async () => {
            await authApi.logout().catch(() => undefined);
            window.location.assign("/connexion");
          }}
        >
          Changer de compte
        </Button>
      }
    >
      {verify.error && (
        <Alert tone="critical" title="Vérification refusée">
          {verify.error.message}
        </Alert>
      )}
      {codeSent && !verify.error && (
        <Alert tone="success" title="Code envoyé">
          Un code vient d'être envoyé à {challenge?.email_hint ?? "votre adresse"}.
        </Alert>
      )}

      <fieldset className="fieldset mt-4">
        <legend className="fieldset__legend">Méthode de vérification</legend>
        {choices.map((choice) => {
          const labels = METHOD_LABELS[choice];
          return (
            <label className="radio" key={choice}>
              <input
                type="radio"
                name="method"
                value={choice}
                checked={method === choice}
                onChange={() => setMethod(choice)}
              />
              <span className="checkbox__text">
                <strong>
                  {labels.title} — {labels.tag}
                </strong>
                <span>{labels.hint}</span>
              </span>
            </label>
          );
        })}
      </fieldset>

      {method === "email" && (
        <Button
          variant="ghost"
          block
          loading={sendCode.pending}
          onClick={() => void sendCode.run()}
        >
          {codeSent ? "Envoyer un nouveau code" : "Envoyer le code par e-mail"}
        </Button>
      )}

      <form
        className="mt-4"
        onSubmit={(event) => {
          event.preventDefault();
          void verify.run();
        }}
        noValidate
      >
        <TextField
          label={method === "recovery" ? "Code de récupération" : "Code de vérification"}
          inputMode={method === "recovery" ? "text" : "numeric"}
          autoComplete="one-time-code"
          autoFocus
          required
          value={code}
          onChange={(event) => setCode(event.target.value)}
          error={verify.fieldErrors.code}
          hint={info.hint}
        />
        <Button
          type="submit"
          variant="primary"
          block
          loading={verify.pending}
          disabled={code.trim().length < 4}
        >
          Vérifier
        </Button>
      </form>

      {challenge?.recovery_codes_remaining !== undefined &&
        challenge.recovery_codes_remaining !== null && (
          <p className="inline-note mt-4 mb-0">
            Codes de récupération restants : {challenge.recovery_codes_remaining}.
          </p>
        )}
    </AuthLayout>
  );
}
